import React from 'react';
import { DispatchProp } from 'react-redux';

import {
    ApiAdapterPromise,
    ApiAdapterRequestConfig,
    ApiResponse,
    rulesEngineSelectors,
    ClassDefinitionContract,
    CharacterPreferences,
    HelperUtils,
    PrerequisiteData,
    Race,
    RaceDefinitionContract,
    RuleData,
} from '@dndbeyond/character-rules-engine/es';

import FormInputField from '../../../../Shared/components/common/FormInputField';
import FormSelectField from '../../../../Shared/components/common/FormSelectField';
import FormToggleField from '../../../../Shared/components/common/FormToggleField';
import { apiCreatorSelectors } from '../../../../Shared/selectors';
import { builderActions } from '../../../actions';
import { Button } from '../../../components/Button';
import ClassChooser from '../../../components/ClassChooser';
import ClassDisplaySimple from '../../../components/ClassDisplaySimple';
import Page from '../../../components/Page';
import PageBody from '../../../components/PageBody';
import PageHeader from '../../../components/PageHeader';
import RaceChooser from '../../../components/RaceChooser';
import RaceDisplaySimple from '../../../components/RaceDisplaySimple';
import { RouteKey } from '../../../constants/navigation';
import { BuilderAppState } from '../../../typings';
import ConnectedBuilderPage from '../ConnectedBuilderPage';

interface Props extends DispatchProp {
    loadRaces: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<RaceDefinitionContract>>>;
    loadClasses: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<ClassDefinitionContract>>>;
    ruleData: RuleData;
    preferences: CharacterPreferences;
    prerequisiteData: PrerequisiteData;
}
interface State {
    level: number | null;
    race: RaceDefinitionContract | null;
    charClass: ClassDefinitionContract | null;
    name: string;
    allowMulticlass: boolean;
    allowFeats: boolean;
    showRace: boolean;
    showClass: boolean;
}
class RandomizeBuild extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            level: null,
            race: null,
            charClass: null,
            name: '',
            allowMulticlass: false,
            allowFeats: false,
            showRace: false,
            showClass: false,
        };
    }

    handleSubmit = () => {
        const { dispatch } = this.props;
        const { level, race, charClass, name, allowMulticlass, allowFeats } = this.state;

        let entityRaceId: number | null = null;
        let entityRaceTypeId: number | null = null;
        if (race) {
            entityRaceId = race.entityRaceId;
            entityRaceTypeId = race.entityRaceTypeId;
        }

        let classId: number | null = null;
        if (charClass) {
            classId = charClass.id;
        }

        dispatch(
            builderActions.randomBuildRequest(
                level,
                entityRaceId,
                entityRaceTypeId,
                classId,
                allowMulticlass,
                allowFeats,
                name,
            ),
        );
    };

    handleRaceSelected = (race: Race): void => {
        this.setState({
            race,
        });
    };

    handleClassSelected = (item: any, amount: any, entity: any): void => {
        this.setState({
            charClass: entity,
        });
    };

    handleRemoveRace = (): void => {
        this.setState({
            race: null,
        });
    };

    handleRemoveClass = (): void => {
        this.setState({
            charClass: null,
        });
    };

    handleNameChange = (name: string): void => {
        this.setState({
            name,
        });
    };

    handleLevelChange = (level: string): void => {
        this.setState({
            level: HelperUtils.parseInputInt(level),
        });
    };

    handleAllowMulticlassChange = (enabled: boolean): void => {
        this.setState({
            allowMulticlass: enabled,
        });
    };

    handleAllowFeatsClick = (enabled: boolean): void => {
        this.setState({
            allowFeats: enabled,
        });
    };

    handleToggleRace = (): void => {
        this.setState((prevState) => ({
            showRace: !prevState.showRace,
        }));
    };

    handleToggleClass = (): void => {
        this.setState((prevState) => ({
            showClass: !prevState.showClass,
        }));
    };

    renderRaceUi = (): React.ReactNode => {
        const { race, showRace } = this.state;
        const { loadRaces, ruleData } = this.props;

        if (race === null) {
            const headerConClasses: Array<string> = ['random-build-header'];
            if (showRace) {
                headerConClasses.push('random-build-header-opened');
            } else {
                headerConClasses.push('random-build-header-collapsed');
            }
            return (
                <div className="builder-field choose-race">
                    <div className={headerConClasses.join(' ')} onClick={this.handleToggleRace}>
                        <div className="random-build-header-heading">
                            <div className="builder-field-heading">Choose Race</div>
                        </div>
                        <div className="random-build-header-trigger" />
                    </div>
                    {showRace && (
                        <RaceChooser
                            loadRaces={loadRaces}
                            onRaceSelected={this.handleRaceSelected}
                            ruleData={ruleData}
                        />
                    )}
                </div>
            );
        } else {
            return <RaceDisplaySimple onRequestAction={this.handleRemoveRace} race={race} ruleData={ruleData} />;
        }
    };

    renderClassUi = (): React.ReactNode => {
        const { charClass, showClass } = this.state;
        const { loadClasses, preferences, prerequisiteData } = this.props;

        if (charClass === null) {
            const headerConClasses: Array<string> = ['random-build-header'];
            if (showClass) {
                headerConClasses.push('random-build-header-opened');
            } else {
                headerConClasses.push('random-build-header-collapsed');
            }
            return (
                <div className="builder-field choose-class">
                    <div className={headerConClasses.join(' ')} onClick={this.handleToggleClass}>
                        <div className="random-build-header-heading">
                            <div className="builder-field-heading">Choose Class</div>
                        </div>
                        <div className="random-build-header-trigger" />
                    </div>
                    {showClass && (
                        <ClassChooser
                            onClassSelected={this.handleClassSelected}
                            loadClasses={loadClasses}
                            preferences={preferences}
                            prerequisiteData={prerequisiteData}
                        />
                    )}
                </div>
            );
        } else {
            return <ClassDisplaySimple onRequestChange={this.handleRemoveClass} charClass={charClass} />;
        }
    };

    render() {
        const { allowMulticlass, allowFeats } = this.state;
        const { ruleData } = this.props;

        const levels = Array.from(new Array(ruleData.maxCharacterLevel), (val, index) => index + 1);

        return (
            <Page clsNames={['random-build']}>
                <PageBody>
                    <PageHeader>Randomize</PageHeader>
                    <FormSelectField
                        clsNames={['random-build-level']}
                        heading="Choose Level"
                        options={levels.map((level) => ({
                            value: level,
                            label: level,
                        }))}
                        onChange={this.handleLevelChange}
                        placeholder={'--'}
                    />
                    {this.renderRaceUi()}
                    {this.renderClassUi()}
                    <FormToggleField
                        heading="Allow Multiclass"
                        toggleLabel="Allow Multiclass"
                        onChange={this.handleAllowMulticlassChange}
                        initiallyEnabled={allowMulticlass}
                    />
                    <FormToggleField
                        toggleLabel="Allow Feats"
                        heading="Allow Feats"
                        onChange={this.handleAllowFeatsClick}
                        initiallyEnabled={allowFeats}
                    />
                    <FormInputField label="Character Name" onChange={this.handleNameChange} />
                    <Button size="large" onClick={this.handleSubmit}>
                        Create Character
                    </Button>
                </PageBody>
            </Page>
        );
    }
}

export default ConnectedBuilderPage(RandomizeBuild, RouteKey.RANDOMIZE_BUILD, (state: BuilderAppState) => {
    return {
        loadRaces: apiCreatorSelectors.makeLoadAvailableRaces(state),
        loadClasses: apiCreatorSelectors.makeLoadAvailableClasses(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        preferences: rulesEngineSelectors.getCharacterPreferences(state),
        prerequisiteData: rulesEngineSelectors.getPrerequisiteData(state),
    };
});
